import { ResourceResponseData, getUploadInfo } from '@/api';

/**
 * The `POST /resources` half of every upload — the step that asks the backend
 * for a resource id and a presigned URL, BEFORE any list row exists.
 *
 * Why this module exists. Five call sites (SourcePanel, TargetPanel,
 * MSSSourceList, useResultUpload, useVoiceFileList) each held their own copy
 * of the same `Promise.all(files.map(getUploadInfo))`, and none of them caught
 * a rejection. That mattered more than a missing try normally would, because
 * the failure lands EARLIER than every surface the app has for reporting one:
 * `setFailed` writes FAILED against a resource id, and on this path there is
 * no resource id and no row to write it against yet. So a backend that
 * refused the request produced no row, no FAILED badge and no message — the
 * dropped files simply vanished, with the rejection reaching only the global
 * error sink, which redacts URLs by design. The failure that is hardest to
 * diagnose was the one with the least to show for it.
 *
 * Two behaviours are fixed here together, because splitting them would be
 * incoherent:
 *
 *  1. FAILURES ARE REPORTED. `requestUploadSlots` never rejects; it separates
 *     the granted slots from the refused files and hands the caller both.
 *     `useUploadSlots` turns the refused list into one acknowledgement.
 *
 *  2. A PARTIAL FAILURE IS PARTIAL. `Promise.all` rejects on the first
 *     refusal, so one 4xx among ten files discarded the other nine — which had
 *     already been granted server-side, and were then never uploaded and never
 *     shown. `Promise.allSettled` keeps the granted ones.
 *
 * The slot carries its own `file` rather than leaving the caller to re-index
 * into the original array. The old call sites paired `resources[i]` with
 * `files[i]`, an alignment that only held while the two arrays were the same
 * length — exactly what (2) stops being true.
 */
export interface UploadSlot {
  /** The file this slot was granted for. */
  file: File;
  /** The backend's resource id + presigned upload URL. */
  data: ResourceResponseData;
}

export interface UploadSlotResult {
  /** Slots the backend granted, in the caller's original file order. */
  granted: UploadSlot[];
  /** Names of the files the backend refused, in the caller's file order. */
  rejectedNames: string[];
}

/**
 * A slot is only a slot if it carries the two fields every caller reads.
 *
 * `getUploadInfo` is TYPED as returning the full envelope, but a type is a
 * claim about the server, not a check on it — and "a 200 whose body is not the
 * shape this client expects" is the first thing a new backend produces. Left
 * untrusted, `data` reads `undefined` here, every caller then evaluates
 * `data.resource_id` while building its row, and the resulting TypeError is
 * thrown INSIDE `addFiles` — which no caller awaits and none wraps in a try.
 * That is the silent-vanish behaviour this module exists to remove, moved one
 * line later: no row, no badge, no acknowledgement.
 *
 * So a malformed success is treated exactly like a refusal. Both fields are
 * checked because both are dereferenced downstream: `resource_id` keys the
 * row and its uploading state, `upload_url` is the PUT target.
 */
const readSlotData = (value: unknown): ResourceResponseData | null => {
  const data = (value as { data?: { data?: Partial<ResourceResponseData> } })
    ?.data?.data;
  if (!data) return null;
  const { resource_id, upload_url } = data;
  if (typeof resource_id !== 'string' || !resource_id) return null;
  if (typeof upload_url !== 'string' || !upload_url) return null;
  return data as ResourceResponseData;
};

/**
 * Request one presigned upload slot per file.
 *
 * Never rejects: a per-file refusal is reported through `rejectedNames`. Each
 * refusal is also logged with the file name, because the reason itself does
 * not survive into the acknowledgement — `console.error` (not `log`) so it
 * survives the production build's console stripping (craco.config.js).
 */
export const requestUploadSlots = async (
  sessionId: string,
  files: File[]
): Promise<UploadSlotResult> => {
  const settled = await Promise.allSettled(
    files.map((file) => getUploadInfo(sessionId, file.name, file.type))
  );

  const granted: UploadSlot[] = [];
  const rejectedNames: string[] = [];
  settled.forEach((result, index) => {
    const file = files[index];
    if (result.status === 'rejected') {
      console.error('failed to create resource', file.name, result.reason);
      rejectedNames.push(file.name);
      return;
    }
    const data = readSlotData(result.value);
    if (!data) {
      console.error(
        'resource response carried no usable slot',
        file.name,
        result.value
      );
      rejectedNames.push(file.name);
      return;
    }
    granted.push({ file, data });
  });
  return { granted, rejectedNames };
};
